import { useEffect, useRef, useState } from 'react';

import { FocusContext, useFocusable } from '../focus';
import { IconNote } from '../icons';
import { colorFor } from '../styles/tokens';
import { Button } from './Button';
import { CoverImage } from './CoverImage';
import { cx } from './cx';
import styles from './HeroCarousel.module.css';

export interface HeroAction {
  key: string;
  label: string;
  primary?: boolean;
}

export interface HeroItem {
  id: string;
  /** Small uppercase label above the title ("LISTA DESTACADA"). */
  kicker?: string;
  title: string;
  subtitle?: string;
  /** `poster_tv` image, shown flat on the right side. */
  imageUrl?: string | null;
  /** Flat background color; derived from the id when omitted. */
  color?: string;
  actions: HeroAction[];
}

export interface HeroCarouselProps {
  items: HeroItem[];
  /**
   * Auto-advance period (docs/04 §3: 8 s), paused while any hero button has focus — which is
   * also when Left/Right step the slides by hand (UX-331).
   */
  intervalMs?: number;
  focusKey?: string;
  onAction: (item: HeroItem, actionKey: string) => void;
  onActive?: () => void;
  className?: string;
}

export function HeroCarousel({
  items,
  intervalMs = 8000,
  focusKey = 'hero',
  onAction,
  onActive,
  className,
}: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const {
    ref,
    focusKey: key,
    hasFocusedChild,
  } = useFocusable<HTMLDivElement>({
    focusKey,
    trackChildren: true,
    saveLastFocusedChild: true,
  });

  const onActiveRef = useRef(onActive);
  useEffect(() => {
    onActiveRef.current = onActive;
  });
  useEffect(() => {
    if (hasFocusedChild) onActiveRef.current?.();
  }, [hasFocusedChild]);

  useEffect(() => {
    if (hasFocusedChild || items.length < 2 || intervalMs <= 0) return undefined;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % items.length), intervalMs);
    return () => window.clearInterval(id);
  }, [hasFocusedChild, items.length, intervalMs]);

  const safeIndex = items.length ? index % items.length : 0;
  const item = items[safeIndex];
  const actionCount = item?.actions.length ?? 0;

  /**
   * UX-331: the hero *is* a carousel — three dots, an 8 s rotation — and the remote could not
   * drive it. Autoplay is paused while the hero has focus and the boot focus **is** the hero's
   * own button, so the default state of the TV was a three-slide carousel frozen on slide 1,
   * with two dots promising slides the remote could not reach. Off the ends of the action row
   * Left/Right now step the slides instead.
   *
   * It steps and does not wrap, for two reasons. The product's edge rule is "no wrap, nudge
   * instead" (UX-011, and the hero already declares itself the cue target), and Left off slide 1
   * has to stay the one press that enters the rail from Home (UX-305/UX-060) — so the first
   * slide's left edge and the last slide's right edge are deliberately left to norigin.
   */
  const step = (delta: number) => {
    setIndex((i) => Math.max(0, Math.min(items.length - 1, i + delta)));
    return false;
  };
  const onActionArrow = (direction: string, position: number) => {
    if (direction === 'left' && position === 0 && safeIndex > 0) return step(-1);
    if (direction === 'right' && position === actionCount - 1 && safeIndex < items.length - 1) {
      return step(1);
    }
    return true;
  };

  if (!item) return null;

  return (
    <FocusContext.Provider value={key}>
      <div
        ref={ref}
        className={cx(styles.hero, className)}
        style={{ background: item.color ?? colorFor(item.id) }}
        data-testid="hero"
        data-index={safeIndex}
        /* UX-011: Right off the last hero action nudges the hero instead of doing nothing. */
        data-edge-cue-target=""
      >
        <div className={styles.text}>
          {item.kicker ? <div className={styles.kicker}>{item.kicker}</div> : null}
          <div className={styles.title}>{item.title}</div>
          {item.subtitle ? <div className={styles.subtitle}>{item.subtitle}</div> : null}
          <div className={styles.actions}>
            {item.actions.map((action, position) => (
              <Button
                key={action.key}
                focusKey={`${focusKey}-${action.key}`}
                variant={action.primary ? 'primary' : 'default'}
                onSelect={() => onAction(item, action.key)}
                onArrowPress={(direction) => onActionArrow(direction, position)}
                className={styles.action}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </div>
        <div className={styles.art}>
          <CoverImage
            src={item.imageUrl}
            sizeHint={560}
            className={styles.poster}
            fallback={<IconNote size={200} className={styles.note} />}
          />
        </div>
        {items.length > 1 ? (
          <div
            className={styles.dots}
            aria-hidden
            data-testid="hero-dots"
            data-count={items.length}
          >
            {items.map((it, i) => (
              <span
                key={it.id}
                aria-hidden
                data-active={i === safeIndex || undefined}
                className={cx(styles.dot, i === safeIndex && styles.dotActive)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </FocusContext.Provider>
  );
}
